// Viajes (recorridos concretos de un vehiculo) y posicion en tiempo real.
// Decision de producto: la app trackea el VEHICULO, nunca a la persona.
// El hueco relevado en Dow es el aviso de demoras (hoy WhatsApp informal):
// por eso el trip modela la demora con reason como dato de primera clase.
import { z } from "zod";
import { Coordinates, Integer, Instant, StopId, SiteId, ProviderId, VehicleType, TripId, } from "./common.js";
export const TripStatus = z
    .enum(["scheduled", "in_progress", "completed", "cancelled"])
    .openapi("TripStatus", {
    description: "Estado del viaje: scheduled, in_progress, completed o cancelled.",
});
// PB-29 (0.6.26): la planta es origen o destino del viaje, no "una parada
// mas". en castellano: planta, subida, bajada.
export const StopKind = z
    .enum(["site", "pickup", "dropoff"])
    .openapi("StopKind", {
    description: "Papel de la parada en el recorrido: site (la planta, origen de un egreso o destino de un ingreso), pickup (subida de pasajeros) o dropoff (bajada). Un traslado libre no tiene site: su primera parada es pickup y la ultima dropoff.",
});
export const TripStop = z
    .object({
    stopId: StopId,
    order: Integer.min(0).openapi({
        description: "Posicion de la parada dentro del recorrido, desde 0. El array de paradas del viaje se entrega ordenado por este campo.",
        example: 0,
    }),
    coordinates: Coordinates,
    estimatedPassTime: Instant.openapi({
        description: "Hora estimada a la que el vehiculo pasa por la parada, en ISO 8601 UTC. Se recalcula ante demoras.",
    }),
    completedAt: Instant.optional().openapi({
        description: "Hora real de paso por la parada, en ISO 8601 UTC: la llegada que marco el conductor (hora del toque, 0.8.0) o, en la demo, la proyeccion GPS del tick. Ausente hasta que el vehiculo la cumple.",
    }),
    kind: StopKind.optional().openapi({
        description: "Papel de la parada (0.6.26): site, pickup o dropoff. Para nombrarla sin mostrar su id: la planta como origen o destino; si hay una sola subida o una sola bajada, origen o destino; si hay varias, parada 1, 2... Ausente en lecturas anteriores a 0.6.26.",
    }),
    vehicleWaitingSince: Instant.optional().openapi({
        description: "Solo en la torre (0.8.0, ADR-029): el vehiculo esta esperando en esta parada desde este instante (la llegada que marco el conductor o la hora planificada, la mas tardia; puede ser futuro si llego temprano). Presente mientras el viaje esta en curso y queda algun pasajero de esta parada sin subir ni marcado ausente. Ausente si no espera o el conductor no marco la llegada.",
    }),
    waitingPassengers: Integer.min(0).optional().openapi({
        description: "Solo con vehicleWaitingSince: cuantos pasajeros de esta parada faltan subir. Solo la cantidad.",
    }),
    skippable: z.boolean().optional().openapi({
        description: "true cuando todos los pasajeros asignados a esta parada avisaron 'hoy no viajo': la parada sigue en el recorrido (no se borra, la trazabilidad la necesita) pero el vehiculo puede omitirla. Derivado de la nomina persistida (ficha y viajes de hoy). Ausente = la parada tiene pasajeros efectivos o el viaje no tiene nomina.",
    }),
})
    .openapi("TripStop", {
    description: "Parada dentro del recorrido de un viaje, con su orden y hora estimada de pasada.",
});
// AT-24 (0.8.3): la parada tal como la lee la torre. Extiende TripStop sin
// tocarlo: TripStop tambien es parada de las respuestas mobile y esas no
// cambian. Solo la usa TripResponse.stops.
export const TowerTripStop = TripStop.extend({
    noShowAvailableAt: Instant.optional().openapi({
        description: "Solo con vehicleWaitingSince (0.8.3, AT-24): desde este instante el conductor puede marcar 'No se presento' (vehicleWaitingSince mas la tolerancia de no-show vigente de la planta, la misma regla que noShowAvailableAt de la hoja del conductor). La torre marca la espera como vencida desde aca, sin recalcular.",
    }),
}).openapi("TowerTripStop", {
    description: "Parada de un viaje en las lecturas de la torre (viajes de hoy y ficha): TripStop mas el vencimiento de la espera.",
});
export const TripDelay = z
    .object({
    delayMin: Integer.positive().openapi({
        description: "Minutos de demora estimados respecto de lo scheduled.",
        example: 15,
    }),
    reason: z.string().min(1).openapi({
        description: "Motivo de la demora, visible para pasajeros y torre.",
        example: "Corte en ruta 3 por obras",
    }),
})
    .openapi("TripDelay", {
    description: "Demora informada de un viaje. Reemplaza el aviso informal por WhatsApp: el dato viaja por la API y llega a todos los afectados.",
});
export const TripResponse = z
    .object({
    tripId: TripId,
    siteId: SiteId,
    vehicleType: VehicleType,
    providerId: ProviderId,
    driver: z.string().min(1).optional().openapi({
        description: "Nombre del chofer asignado, si el proveedor ya lo definio. El reparto de recorridos entre choferes es logistica del proveedor.",
        example: "Juan Perez",
    }),
    stops: z.array(TowerTripStop).openapi({
        description: "Paradas del recorrido, ordenadas por el campo order. El armado respeta el tope de trayecto por pasajero (invariante del dominio).",
    }),
    status: TripStatus,
    delay: TripDelay.optional().openapi({
        description: "Demora vigente del viaje, si la hay.",
    }),
    itinerary: z.array(Coordinates).optional().openapi({
        description: "Polilinea por calles del recorrido completo (primera a ultima parada), si el ruteo estuvo disponible. Torre v2: el polling de /trips/today ya NO la incluye; se sirve sola en GET /trips/{tripId}/itinerary (inmutable, cacheable).",
    }),
    etaDestination: Instant.optional().openapi({
        description: "ETA a la ultima parada del viaje, recalculada por tick con la demora activa, en ISO 8601 UTC.",
    }),
    vehicleId: z.string().min(1).optional().openapi({
        description: "Identificador del vehiculo asignado al viaje, si ya se conoce.",
        example: "shuttle-01",
    }),
    capacity: Integer.positive().optional().openapi({
        description: "Capacidad del vehiculo asignado (asientos). Con la nomina del viaje habilita el conteo n/N de la torre.",
        example: 19,
    }),
    effectivePassengers: z.number().int().min(0).optional().openapi({
        description: "Pasajeros que siguen contando para el viaje: los de la nomina que NO avisaron 'hoy no viajo' (assigned, onboard o no_show). Es el n del n/N de la torre. Ausente cuando el viaje no tiene nomina persistida.",
        example: 11,
    }),
})
    .openapi("TripResponse", {
    description: "Viaje tal como lo ven torre, proveedor y pasajeros. Multi-tenant: pertenece a una planta (siteId).",
});
export const TodayTripsResponse = z.array(TripResponse).openapi("TodayTripsResponse", {
    description: "Viajes de hoy del tenant, sin la polilinea (que se pide aparte por viaje).",
});
export const TodayTripsQuery = z
    .object({
    status: TripStatus.optional().openapi({
        description: "Filtro opcional por estado del viaje.",
    }),
})
    .openapi("TodayTripsQuery", { description: "Filtros de GET /trips/today." });
export const TripParams = z
    .object({ tripId: TripId })
    .openapi("TripParams", { description: "Parametro de path :tripId." });
export const PositionSource = z
    .enum(["driver_app", "third_party_telemetry"])
    .openapi("PositionSource", {
    description: "Origen del dato de posicion: la app del chofer o la telemetria de un tercero (por ejemplo el GPS ya instalado en la flota).",
});
export const VehiclePosition = z
    .object({
    tripId: TripId,
    coordinates: Coordinates,
    instant: Instant.openapi({
        description: "Instant de la medicion, en ISO 8601 UTC.",
    }),
    source: PositionSource,
})
    .openapi("VehiclePosition", {
    description: "Posicion en tiempo real del VEHICULO de un viaje. Decision de diseno: se trackea el vehiculo, nunca a la persona; no hay datos de ubicacion de pasajeros ni del chofer fuera del viaje.",
});
// PB-5: el simulador del laboratorio escribe posiciones con origen propio. El
// valor NO entra en PositionSource ni en VehiclePosition porque esos dos viajan
// a la app del pasajero (MobileTripPositionResponse) y los modelos Kotlin
// generados tratan los enums como cerrados: un valor nuevo romperia la app
// instalada. La torre y la consola leen la variante Tracked, que si lo trae.
export const TrackedPositionSource = z
    .enum(["driver_app", "third_party_telemetry", "simulated"])
    .openapi("TrackedPositionSource", {
    description: "Origen de una posicion registrada, visto por la torre y la consola: app del conductor, telemetria de un tercero o `simulated` (simulador de movimiento de la planta de laboratorio, nunca de una planta real).",
});
export const TrackedVehiclePosition = z
    .object({
    tripId: TripId,
    coordinates: Coordinates,
    instant: Instant.openapi({
        description: "Instant de la medicion, en ISO 8601 UTC.",
    }),
    source: TrackedPositionSource,
})
    .openapi("TrackedVehiclePosition", {
    description: "Posicion del VEHICULO de un viaje tal como la ve la torre, con el origen completo (incluido el simulado). Mismo criterio que VehiclePosition: se trackea el vehiculo, nunca a la persona.",
});
export const PositionsResponse = z.array(TrackedVehiclePosition).openapi("PositionsResponse", {
    description: "Ultima posicion de cada viaje en curso del tenant.",
});
// PP-33 (PLAN-ASIGNACION-PROVEEDOR §8). Vive aca y no en provider-trips.ts
// porque el vencimiento del cierre diario (tower.ts) tambien lo nombra.
export const AssignmentExceptionType = z
    .enum(["assignment_expired", "assignment_queue_exhausted", "assignment_ineligible", "assignment_changed_in_progress", "assignment_missing_in_progress"])
    .openapi("AssignmentExceptionType", {
    description: "Novedades de la asignacion (valores de exception_type): assignment_expired (warning, se reofrecio), assignment_queue_exhausted (critical; exceptuada del vencimiento del cierre diario, solo la cierra una persona), assignment_ineligible (warning), assignment_changed_in_progress (warning) y assignment_missing_in_progress (critical).",
});
//# sourceMappingURL=trips.js.map